// 시공사례 설정 — 로그인 없는 공개 페이지를 열지(기본 끔) · 그 화면의 「상담 문의」 버튼(기본 켬) · 홈페이지에 붙일 iframe 코드.

import { Copy } from "lucide-react";
import { useState, useTransition } from "react";
import { toast } from "sonner";

import { InstantSaveNote } from "@/components/save-bar";
import SectionCard from "@buildpy/ui/section-card";
import { Button } from "@buildpy/ui/button";
import { Switch } from "@buildpy/ui/switch";
import { Textarea } from "@buildpy/ui/textarea";
import { formatNumber } from "@buildpy/core/lib/format";
import { settleRedirect } from "@/lib/redirect-error";
import type { SettingsResult } from "@buildpy/core/modules/org/action-types";
import { savePortfolioPublicInquiry, savePortfolioPublicPage } from "@/api/actions/org";

interface Props {
  canEdit: boolean;
  enabled: boolean;
  // 공개 화면의 「상담 문의」 버튼(`portfolio.public_inquiry`, 기본 켬).
  inquiryEnabled: boolean;
  // 직원이 숨긴 고객 후기 수(`0044`) — 공개 페이지에 안 나가는 것을 여기서 알린다.
  hiddenReviews: number;
  // 홈페이지에 붙일 iframe 코드 — 회사 주소 이름이 없으면 null.
  embedCode?: string | null;
}

// 스위치 하나 — 먼저 바꿔 보이고, 저장이 실패하면 되돌린다.
export const useSavedSwitch = (initial: boolean, save: (next: boolean) => Promise<SettingsResult>) => {
  const [checked, setChecked] = useState(initial);
  const [pending, startTransition] = useTransition();

  const onChange = (next: boolean) => {
    const prev = checked;
    setChecked(next);
    startTransition(async () => {
      try {
        const result = await save(next);
        if (result.ok) {
          toast.success(result.message);
        } else {
          setChecked(prev);
          toast.error(result.message);
        }
      } catch (caught) {
        if (settleRedirect(caught)) return;
        setChecked(prev);
        toast.error("저장하지 못했습니다. 잠시 후 다시 시도하세요.");
      }
    });
  };

  return { checked, pending, onChange };
};

const EmbedCode = ({ code }: { code: string }) => {
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      toast.success("코드를 복사했습니다");
    } catch {
      toast.error("복사하지 못했습니다");
    }
  };

  return (
    <div className="mt-3 flex flex-col gap-2 border-t pt-3" data-testid="settings-portfolio-embed">
      <label className="text-sm font-medium" htmlFor="settings-portfolio-embed-code">
        홈페이지에 붙이기
      </label>
      <p className="text-xs text-muted-foreground">
        아래 코드를 홈페이지 HTML 에 붙이면 공개 사례 목록이 보입니다. 높이가 고정이라 사례가 많으면 상자 안에서 스크롤됩니다. 사례를 누르면 새 탭으로 열립니다.
      </p>
      <Textarea
        className="font-mono text-xs phone:text-xs"
        data-testid="settings-portfolio-embed-code"
        id="settings-portfolio-embed-code"
        readOnly
        rows={3}
        value={code}
        onFocus={(event) => event.currentTarget.select()}
      />
      <div>
        <Button data-testid="settings-portfolio-embed-copy" size="sm" type="button" variant="outline" onClick={copy}>
          <Copy className="size-4" />
          코드 복사
        </Button>
      </div>
    </div>
  );
};

const SettingsPortfolio = ({ canEdit, enabled, inquiryEnabled, hiddenReviews, embedCode }: Props) => {
  const page = useSavedSwitch(enabled, savePortfolioPublicPage);
  const inquiry = useSavedSwitch(inquiryEnabled, savePortfolioPublicInquiry);

  return (
    <SectionCard
      data-testid="settings-portfolio"
      description="켜면 「공개 페이지에 올립니다」로 둔 사례를 로그인 없이 누구나 볼 수 있습니다. 끄면 시공사례 화면에서 만든 공개 링크까지 모든 주소가 닫힙니다."
      actions={canEdit ? <InstantSaveNote /> : undefined}
      title="시공사례 공개 페이지">
      <label className="flex min-h-10 items-center gap-x-2 text-sm" htmlFor="portfolio-public-page">
        <Switch
          aria-describedby="portfolio-public-page-label"
          aria-label="시공사례 공개 페이지"
          checked={page.checked}
          disabled={!canEdit || page.pending}
          id="portfolio-public-page"
          onCheckedChange={page.onChange}
        />
        <span id="portfolio-public-page-label">{page.checked ? "공개 페이지를 엽니다" : "공개 페이지를 닫아 둡니다"}</span>
      </label>
      <label className="flex min-h-10 items-center gap-x-2 text-sm" htmlFor="portfolio-public-inquiry">
        <Switch
          aria-describedby="portfolio-public-inquiry-label"
          aria-label="상담 문의 버튼 보이기"
          checked={inquiry.checked}
          data-testid="settings-portfolio-inquiry"
          disabled={!canEdit || inquiry.pending}
          id="portfolio-public-inquiry"
          onCheckedChange={inquiry.onChange}
        />
        <span id="portfolio-public-inquiry-label">{inquiry.checked ? "「상담 문의」 버튼을 보입니다" : "「상담 문의」 버튼을 숨깁니다"}</span>
      </label>
      <p className="text-sm text-muted-foreground">
        버튼을 숨겨도 홈페이지 견적문의 등으로 들어오는 간편접수 주소는 그대로 열립니다.
      </p>
      {page.checked && embedCode && <EmbedCode code={embedCode} />}
      {hiddenReviews > 0 && (
        <p className="mt-2 text-sm text-muted-foreground" data-testid="settings-hidden-reviews">
          숨긴 후기 {formatNumber(hiddenReviews)}건 — 각 현장 화면의 「고객 후기」에서 다시 보이게 할 수 있습니다.
        </p>
      )}
    </SectionCard>
  );
};

export default SettingsPortfolio;
